import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import { AppShell } from '@/components/layout/app-shell'
import { partnerApi } from '@/lib/api'

export function AuditHistoryPage() {
  const audit = useQuery({ queryKey: ['audit-history'], queryFn: () => partnerApi.auditHistory(100) })

  const handleExport = async (kind: 'full' | 'audit' | 'personal') => {
    try {
      const r = await partnerApi.requestDataExport(kind)
      toast.success(`Export ${kind} solicitado: ${r.export_id.slice(0, 8)}...`)
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo solicitar: ${e?.response?.data?.message ?? 'error desconocido'}`)
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-bold">Audit / Habeas Data</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Historial de aceptaciones legales. Cada fila es inmutable (append-only DB-level + hash chain SHA-256).
          Bajo Ley 1581/2012 puedes solicitar export de toda tu data.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={() => handleExport('full')} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-bg-elevated">
            <Download className="h-3 w-3" /> Solicitar export completo
          </button>
          <button onClick={() => handleExport('audit')} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-bg-elevated">
            <Download className="h-3 w-3" /> Solo audit log
          </button>
          <button onClick={() => handleExport('personal')} className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-bg-elevated">
            <Download className="h-3 w-3" /> Solo PII (datos personales)
          </button>
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-bg-surface">
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated">
              <tr>
                <Th>Fecha (UTC)</Th>
                <Th>Contrato</Th>
                <Th>Versión</Th>
                <Th>IP</Th>
                <Th>User-Agent</Th>
                <Th>Hash (SHA-256)</Th>
              </tr>
            </thead>
            <tbody>
              {audit.isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-fg-muted">Cargando...</td></tr>
              )}
              {audit.isError && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-red-700 dark:text-red-300">
                    No se pudo cargar el historial de aceptaciones. Recarga la página o inténtalo más tarde.
                  </td>
                </tr>
              )}
              {audit.data?.items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-fg-muted">Sin aceptaciones registradas todavía</td></tr>
              )}
              {audit.data?.items.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-4 py-3 text-fg-secondary">{new Date(row.accepted_at).toISOString().replace('T', ' ').replace('.000Z', 'Z')}</td>
                  <td className="px-4 py-3 font-medium">{row.contract_kind === 'terms' ? 'Términos' : 'Mandato'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{row.contract_version}</td>
                  <td className="px-4 py-3 font-mono text-xs">{row.ip_address}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-xs text-fg-muted" title={row.user_agent}>{row.user_agent}</td>
                  <td className="px-4 py-3 font-mono text-[10px] text-fg-muted">{row.this_hash.slice(0, 16)}...</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left font-semibold text-fg-secondary">{children}</th>
}
